(ns hypnos.dme.tile.consult
  "The consult tile."
  (:require [hypnos.dme.core.patient :as core-patient]
            [hypnos.dme.subs]
            [hypnos.dme.tile.field.consult-ahi :as consult-ahi]
            [hypnos.dme.tile.field.consult-mask :as consult-mask]
            [hypnos.dme.tile.field.consult-mask-comfortable :as consult-mask-comfortable]
            [hypnos.dme.tile.field.consult-modality :as consult-modality]
            [hypnos.dme.tile.field.consult-pressure :as consult-pressure]
            [hypnos.dme.tile.field.consult-service-type :as consult-service-type]
            [hypnos.dme.tile.field.consult-therapist :as consult-therapist]
            ;; [hypnos.dme.tile.field.patient-dob :as patient-dob]
            [hypnos.dme.tile.table.survey :as survey]
            [hypnos.graphql.mutations :as mutations]
            [hypnos.i18n :refer [tr]]
            [hypnos.translator.patient :as t-p]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- load
  [body]
  (if-let [_loading? @(rf/subscribe [:dme/gql-request-in-progress?])]
    [:div.card-content
     [:section.section
      [:div.content.has-text-grey.has-text-centered
       [:p
        [:span.icon.is-large
         [:i.mdi.mdi-loading.mdi-48px.mdi-spin]]]
       [:p (tr :ui.label/loading)]]]]
    body))

(defn- warning-notification
  "Show warning notification for new consults when the following citeria is met."
  [state]
  (let [modality (get-in @state [:patient :patient/last-consult :consult/modality :value])
        cpap     (get-in @state [:patient :patient/last-consult :consult/cpap :value])
        ipap     (get-in @state [:patient :patient/last-consult :consult/ipap :value])
        epap     (get-in @state [:patient :patient/last-consult :consult/epap :value])
        ahi      (get-in @state [:patient :patient/last-consult :consult/ahi :value])]
    (cond
      (or (nil? modality)
          (and (= :modality/cpap modality) (nil? cpap))
          (and (= :modality/bipap-vpap modality) (nil? ipap) (nil? epap)))
      [:article.message.is-warning [:div.message-body (tr :ui.warning/pressure)]]
      (and (= :modality/autopap-smartpap modality) (nil? ahi))
      [:article.message.is-warning [:div.message-body (tr :ui.warning/ahi)]])))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; API

(defn tile
  "The consult tile."
  [state]
  (let [organization (rf/subscribe [:dme/organization])
        new-patient? (core-patient/new-patient? state)]
    [:div.tile.is-ancestor
     [:div.tile.is-parent
      [:div.card.tile.is-child
       [:header.header.card-header
        [:p.card-header-title (tr :ui.label/new-consult)]]
       (load
        [:div.card-content
         [consult-therapist/field state]
         ;; patient info
         [consult-service-type/field state]
         [consult-mask/field state]
         [consult-mask-comfortable/field state]
         [:hr]
         ;; clinical info
         [warning-notification state]
         [consult-ahi/field state]
         [consult-modality/field state]
         [consult-pressure/cpap-field state]
         [consult-pressure/ipap+epap-field state]
         ;; survey answers info
         [:div
          [:hr]
          [:button.button.is-success.is-inverted
           {:on-click (fn [_]
                          (let [therapist-assisted-assessment? (:therapist-assisted-assessment? @state)]
                               (swap! state assoc :therapist-assisted-assessment? (not therapist-assisted-assessment?))))}
           [:span.icon.is-small
            (if (:therapist-assisted-assessment? @state)
              [:i.mdi.mdi-checkbox-marked-circle-outline]
              [:i.mdi.mdi-circle-outline])]
           [:span (tr :ui.label/therapist-assisted-assessment)]]
          (when (:therapist-assisted-assessment? @state)
            [:div
             [survey/instructions]
             [survey/answers state]])]
         [:hr]
         [:div.field.is-horizontal
          [:div.field-label]
          [:div.field-body
           [:div.field
            [:div.field.is-grouped
             [:div.control
              [:a.button.is-info
               {:href     "#recommendations"
                :disabled (not (or (get-in @state [:patient :patient/phone-number :valid?])
                                   (get-in @state [:patient :patient/last-consult :consult/service-type :value])))
                :on-click (fn [_]
                            (let [org          (dissoc @organization :user/_organizations)
                                  patient      (-> (t-p/patient->db @state)
                                                   (dissoc :patient/image
                                                           :patient/mask
                                                           :patient/selector
                                                           :patient/selector-type
                                                           :patient/anatomy
                                                           :patient/recommendations
                                                           :patient/last-consult)
                                                   (assoc :patient/organization org))
                                  consult-keys [;; consult info
                                                :consult/service-type
                                                :consult/ahi
                                                :consult/modality
                                                :consult/cpap
                                                :consult/ipap
                                                :consult/epap
                                                :consult/manufacturer
                                                :consult/make
                                                :consult/model
                                                :consult/mask-was-comfortable?
                                                :consult/user]
                                  consult-keys (if (:therapist-assisted-assessment? @state)
                                                 (concat consult-keys
                                                         [;; survey answers
                                                          :consult/weight
                                                          :consult/take-dentures-out?
                                                          :consult/sleep-positions
                                                          :consult/sleep-motion?
                                                          :consult/mouth-breathing-level
                                                          :consult/eye-dryness?
                                                          :consult/claustrophobia-level
                                                          :consult/issues
                                                          :consult/deviated-septum?
                                                          :consult/chronic-issues?
                                                          :consult/pacemaker?])
                                                 consult-keys)
                                  consult      (select-keys (t-p/consult->db @state) consult-keys)]
                              ;; submit information
                              (rf/dispatch-sync [:amplify/mutation
                                                 mutations/create-consult
                                                 {:patient (t-p/patient->gql patient)
                                                  :consult (t-p/consult->gql consult)}
                                                 :dme/set-patient
                                                 :dme/set-error])
                              ;; reset new-consult flag
                              (swap! state assoc :new-consult? false)))}
               [:span (tr :ui.label/submit)]]]
             [:div.control
              (if new-patient?
                [:a.button.is-info.is-outlined
                 {:href "/dashboard"}
                 [:span (tr :ui.label/cancel)]]
                [:button.button.is-info.is-outlined
                 {:on-click (fn [_] (swap! state assoc :new-consult? false))}
                 [:span (tr :ui.label/cancel)]])]]]]]])]]]))
